import { navigateTo, useRoute } from '#imports'
import type { Ref } from 'vue'
import type { RouteLocationNormalized } from 'vue-router'

import type { GetPickersResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/pickers'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'

import { useMasterPP } from '~/scopes/product-core/composables/useMasterPP/useMasterPP'
import { useUrlParams } from '~/scopes/product-core/composables/useUrlParams/useUrlParams'

/**
 * The product pages relies on the URL to know its current state, including the currently selected grade, which lies
 * in the URL hash (i.e.: #l=10).
 *
 * When the page is accessed without any hash, we add the grade based on the default selected grade for the product. This happens quite
 * often from linking in our app or by SEO access.
 */
export async function useAddGradeInUrl(
  selectedOffer: Ref<GetPickersResponse['selectedOffer'] | null | undefined>,
  route: RouteLocationNormalized = useRoute(),
) {
  const { grade } = useUrlParams(route)
  const { isMasterPPUrl } = useMasterPP(route)
  const logger = useLogger()

  if (grade.value || !selectedOffer.value || isMasterPPUrl.value) {
    return undefined
  }

  logger.info('[PRODUCT] Add missing grade in URL', {
    owners: ['bot-squad-product-page-front'],
  })

  const link = {
    ...route,
    type: 'internal',
    query: {
      ...route.query,
      l: selectedOffer.value.grade.value.toString(),
    },
    hash: route.hash,
    replace: true,
  }

  return navigateTo(link)
}
